import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { mockData } from '../mock';
import { siteConfig, inquiryTypes } from '../config/site';
import { sendEnquiry } from '../lib/sendEnquiry';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    inquiryType: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.inquiryType) {
      toast.error('Please select an inquiry type.');
      return;
    }

    setSubmitting(true);
    try {
      await sendEnquiry({ ...formData, source: 'contact-page' });
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', inquiryType: '', message: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-background text-foreground pt-36 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20 text-center"
        >
          <p className="corporate-eyebrow mb-4">Contact</p>
          <h1 className="text-4xl md:text-6xl font-semibold mb-6">Get in Touch</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Have questions or want to learn more? We'd love to hear from you.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="bg-card border border-border p-10">
              <h2 className="text-3xl font-bold mb-8 text-[#D4AF37] tracking-tight">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name *</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-background border-border text-foreground"
                    style={{ borderRadius: '0px' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email Address *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-background border-border text-foreground"
                    style={{ borderRadius: '0px' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Inquiry Type *</label>
                  <Select
                    value={formData.inquiryType}
                    onValueChange={(value) =>
                      setFormData({ ...formData, inquiryType: value })
                    }
                  >
                    <SelectTrigger 
                      className="bg-background border-border text-foreground"
                      style={{ borderRadius: '0px' }}
                    >
                      <SelectValue placeholder="Select inquiry type" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {inquiryTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="bg-background border-border text-foreground"
                    style={{ borderRadius: '0px' }}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#c5a028] text-lg py-6 disabled:opacity-60"
                  style={{ borderRadius: '0px' }}
                >
                  {submitting ? 'Sending…' : 'Send Message'}
                </Button>
              </form>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div className="bg-card border border-border p-8">
              <Mail size={32} className="text-[#D4AF37] mb-4" />
              <h3 className="text-xl font-semibold mb-2">Email Us</h3>
              <p className="text-muted-foreground mb-2">
                <a href={`mailto:${siteConfig.emails.legal}`} className="hover:text-[#B8941F] transition-colors">
                  {siteConfig.emails.legal}
                </a>
              </p>
              <p className="text-muted-foreground mb-2">
                <a href={`mailto:${siteConfig.emails.business}`} className="hover:text-[#B8941F] transition-colors">
                  {siteConfig.emails.business}
                </a>
              </p>
              <p className="text-muted-foreground">
                <a href={`mailto:${siteConfig.emails.info}`} className="hover:text-[#B8941F] transition-colors">
                  {siteConfig.emails.info}
                </a>
              </p>
            </div>

            <div className="bg-card border border-border p-8">
              <MapPin size={32} className="text-[#D4AF37] mb-4" />
              <h3 className="text-xl font-bold mb-2 tracking-tight">Headquarters</h3>
              <p className="text-muted-foreground">1075 Terra Bella Ave</p>
              <p className="text-muted-foreground">Mountain View, CA 94043</p>
              <p className="text-muted-foreground">United States</p>
            </div>

            <div className="bg-card border border-border p-8">
              <MapPin size={32} className="text-[#D4AF37] mb-4" />
              <h3 className="text-xl font-bold mb-2 tracking-tight">Asia Headquarters</h3>
              <p className="text-muted-foreground">23 Sukhumvit Soi 13</p>
              <p className="text-muted-foreground">Khlong Toei Nuea, Bangkok 10110</p>
              <p className="text-muted-foreground">Thailand</p>
            </div>
          </motion.div>
        </div>

        {/* Global Offices */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl font-bold mb-12 text-center text-[#D4AF37] tracking-tight">Global Offices</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.offices.map((office, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-card border border-border p-6 hover:border-[#D4AF37] transition-all duration-400"
              >
                <h3 className="font-bold mb-2 text-[#D4AF37]">{office.country}</h3>
                <p className="text-muted-foreground text-sm">{office.address}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;